<template>
  <x-page
    :loading="loading"
    transparent-header
    enable-header-parallax
    white-back
  >
    <div :class="_.pageWrap">
      <div :class="_.header">
        <!-- 轮播区域 -->
        <SwipeSection :list="list" />

        <!-- 搜索区域 -->
        <SearchSection />
      </div>

      <FlexShrinkWrapper>
        <!-- 分类区域 -->
        <CategorySection
          :categories="categories"
          :default-active="defaultActive"
          @change="handleChange"
        />

        <FlexShrinkWrapper>
          <!-- 提示区域 -->
          <NoticeSection />

          <!-- 侧边分类区域 -->
          <TreeSelectSection
            :categories="categories"
            :default-active="defaultActive"
            @subchange="handleSubChange"
          >
            <!-- 筛选插槽 -->
            <template #filter>
              <FilterTabs
                :key="filterTabsKey"
                :class="_.bdb"
                :default-active="activeTab"
                @sort-change="handleSortChange"
              />
            </template>

            <!-- 商品列表插槽 -->
            <template #list>
              <div ref="listWrap" :class="[_.listWrap, enableIOS && _.ios]">
                <x-waterfall
                  v-if="categoryId"
                  ref="list"
                  empty-text="产品陆续上架中"
                  :load="handleLoad"
                >
                  <template #default="{ data }">
                    <ProductSection
                      v-for="item in data"
                      :key="item.commodityId"
                      :product="item"
                      @clickService="handleProductClick"
                    />
                  </template>
                </x-waterfall>
              </div>
            </template>
          </TreeSelectSection>
        </FlexShrinkWrapper>
      </FlexShrinkWrapper>
    </div>
  </x-page>
</template>

<script>
  import CategorySection from './components/CategorySection.vue'
  import FilterTabs from './components/FilterTabs.vue'
  import FlexShrinkWrapper from './components/FlexShrinkWrapper.vue'
  import NoticeSection from './components/NoticeSection.vue'
  import ProductSection from './components/ProductSection.vue'
  import SearchSection from './components/SearchSection.vue'
  import SwipeSection from './components/SwipeSection.vue'
  import TreeSelectSection from './components/TreeSelectSection.vue'
  import Vue from 'vue'
  import {
    apiCommodityCommodityCategoryCommodityList,
    apiCommodityFrontCategoryGetWebCategoryTree,
    apiPageConfigGetStateContentByApp,
  } from '@/api'

  const STATE_ID_MAP = {
    // 自营StateID
    0: '1958717814292541440',
    // 京东StateID
    1: '1958717263785947136',
  }

  // 排序类型映射
  const SORT_TYPE_MAP = {
    comprehensive: 0, // 综合
    sales: 1, // 销量
    price: 2, // 价格
  }

  // 排序方式映射
  const SORT_ORDER_MAP = {
    asc: 0, // 升序
    desc: 1, // 降序
  }

  export default Vue.extend({
    components: {
      SwipeSection,
      SearchSection,
      CategorySection,
      NoticeSection,
      TreeSelectSection,
      FlexShrinkWrapper,
      FilterTabs,
      ProductSection,
    },

    data: () => ({
      loading: false,
      list: [],
      categories: [], // 分类数据
      products: [],
      defaultActive: '',
      activeTab: 'comprehensive',
      categoryId: '',
      sortOrderByType: 0,
      sortType: 0,
      filterTabsKey: 0, // 用于强制重新渲染FilterTabs组件
      scrollTop: 0,
    }),

    created() {
      // 组件创建时检测一次iOS环境
      this.isIOS = require('vtils').inIOS()
    },

    mounted() {
      this.initializeData()
    },
    activated() {
      // 激活时恢复滚动位置
      this.$nextTick(() => {
        this.$refs.listWrap.scrollTop = this.scrollTop
      })
    },

    methods: {
      handleProductClick() {
        this.scrollTop = this.$refs.listWrap.scrollTop
      },
      // 初始化页面数据
      async initializeData() {
        try {
          // 并行加载分类数据和轮播数据
          await Promise.all([this.getCategoryData(), this.getSwipeData()])
        } catch (error) {
          console.warn('页面初始化失败:', error)
        }
      },

      // 获取商品列表数据
      async handleLoad({ pageNumber }) {
        try {
          const data = await apiCommodityCommodityCategoryCommodityList(
            {
              categoryId: this.categoryId, // 二级分类ID
              pageIndex: pageNumber,
              pageSize: 10,
              pageType: this.$route.query.pageType || 0, // 页面类型 0多彩宝 1京东
              sortOrderByType: this.sortOrderByType,
              sortType: this.sortType,
            },
            {
              noErrorToast: true,
            },
          ).then(res => {
            return res
          })

          return data
        } catch {}
      },

      // 获取商品分类数据
      async getCategoryData() {
        try {
          const { categoryId = '' } = this.$route.query
          const data = await apiCommodityFrontCategoryGetWebCategoryTree({
            categoryId,
          })

          if (data.length) {
            this.categories = data
            // 路由参数有分类selectId时，默认选中
            const { selectId } = this.$route.query

            if (selectId) {
              const category = data.find(({ id }) => id === selectId)
              this.defaultActive = category?.id

              if (category.children.length) {
                this.categoryId = category.children[0].id
              }
            } else {
              this.defaultActive = data[0].id

              // 默认二级分类
              if (data[0].children.length) {
                this.categoryId = data[0].children[0].id
              }
            }
          }
        } catch {}
      },

      // 获取轮播组件数据
      async getSwipeData() {
        try {
          const { pageType = 0 } = this.$route.query

          const params = {
            stateId: STATE_ID_MAP[pageType],
            pageIndex: 1,
            pageSize: 10,
          }
          const data = await apiPageConfigGetStateContentByApp(params)

          this.list = data.list
        } catch {}
      },

      // 统一的重置筛选方法
      resetFilters() {
        this.activeTab = 'comprehensive'
        this.sortOrderByType = 0
        this.sortType = 0
        // 强制FilterTabs组件重置
        this.filterTabsKey += 1
      },

      // 切换类目
      handleChange({ id }) {
        this.defaultActive = id

        // 默认选中二级分类
        if (this.categories.length) {
          const list = this.categories.find(
            category => category.id === id,
          ).children
          this.categoryId = list[0].id

          // 重置筛选
          this.resetFilters()
          // 触发数据重新加载
          this.$refs.listWrap.scrollTop = 0
          this.$refs.list?.reload()
        }
      },

      // 子分类切换
      handleSubChange({ id }) {
        this.categoryId = id

        // 重置筛选
        this.resetFilters()
        this.$refs.listWrap.scrollTop = 0
        // 触发数据重新加载
        this.$refs.list?.reload()
      },

      // 筛选排序 - 优化版本
      handleSortChange({ type, order }) {
        // 更新排序类型
        if (Object.prototype.hasOwnProperty.call(SORT_TYPE_MAP, type)) {
          this.sortType = SORT_TYPE_MAP[type]
        }

        if (order in SORT_ORDER_MAP) {
          // 判断筛选综合排序时，sortOrderByType = 0
          if (type === 'comprehensive') {
            this.sortOrderByType = 0
          } else {
            this.sortOrderByType = SORT_ORDER_MAP[order]
          }
        }
        this.$refs.listWrap.scrollTop = 0
        // 触发数据重新加载
        this.$refs.list?.reload()
      },
    },
  })
</script>

<style lang="scss" module>
  .pageWrap {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .header {
    flex-shrink: 0;
  }

  .bdb {
    border-bottom: 1px solid rgba(152, 152, 152, 0.1);
    flex-shrink: 0;
    position: relative;
    z-index: 100;
  }

  .listWrap {
    overflow-y: auto;
    height: 100%;
    box-sizing: border-box;

    &.ios {
      padding-bottom: 40px;
    }

    // 隐藏滚动条
    &::-webkit-scrollbar {
      display: none;
    }
  }
</style>
